import { effect, inject, Injectable, Signal, signal } from '@angular/core';
import { Observable, EMPTY } from 'rxjs';
import { take, tap, catchError } from 'rxjs/operators';
import { IAPIListResponse, IListOptions, ITranslateDocumentInput, ITranslateDocumentOutput, ITranslateKeyValuePayload, ITranslateTextPayload, ITranslationAttempt, TranslationJobStatus } from '@newroom-connect/library/interfaces';
import { FileProcessingStatus } from '@newroom-connect/library/types';

import { EntityService } from '../entity/entity.service';
import { ListService } from '../list/list.service';
import { ProjectService } from '../project/project.service';
import { FileService } from '../file';
import { FileProcessingService } from '../file/file-processing.service';

@Injectable({
  providedIn: 'root'
})
export class AutoTranslateService extends EntityService {
  public static readonly SUPPORTED_FILE_TRANSLATION_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ];

  private readonly projectService = inject(ProjectService);
  private readonly listService = inject(ListService);
  private readonly fileProcessingService = inject(FileProcessingService);

  protected searchableProperties: string[] = ['fileId'];

  public activeDocumentTranslation = signal<ITranslationAttempt[]>([]);
  public currentFileIdSig = signal<string | undefined>(undefined);

  private projectId?: string;

  /**
   * @constructor
   */
  constructor() {
    super();

    effect(() => {
      const project = this.projectService.watchCurrentProject()();
      const fileId = this.currentFileIdSig();

      if (!project) {
        return;
      }

      this.projectId = project.id;
      this.fetchProjectTranslateDocumentJob(fileId);
    }, { allowSignalWrites: true });
  }

  /**
   * Translates the values of the keys in the provided JSON object.
   *
   * @param projectId
   * @param autoTranslateParams
   *
   * @returns
   */
  public translateKeyValue(projectId: string, autoTranslateParams: ITranslateKeyValuePayload): Observable<ITranslateKeyValuePayload> {
    const translationParams = {
      from: autoTranslateParams.from,
      to: autoTranslateParams.to,
      payload: autoTranslateParams.payload
    };

    return this.apiService.post<ITranslateKeyValuePayload>(`projects/${projectId}/translate/json`, translationParams);
  }

  /**
   * Translates text content.
   *
   * @param projectId
   * @param autoTranslateParams
   * @returns
   */
  public translateText(projectId: string, autoTranslateParams: ITranslateTextPayload): Observable<ITranslateTextPayload> {
    const translationParams = {
      from: autoTranslateParams.from,
      to: autoTranslateParams.to,
      text: autoTranslateParams.text
    };

    return this.apiService.post<ITranslateTextPayload>(`projects/${projectId}/translate/text`, translationParams);
  }

  /**
   * Translates documents using SSE for progress tracking.
   *
   * @param projectId
   * @param filename
   * @param translateDocParams
   * @param sessionId
   * @returns
   */
  public translateDocuments(projectId: string, filename: string, translateDocParams: ITranslateDocumentInput, sessionId: string): Observable<ITranslateDocumentOutput> {
    // Build URL with sessionId query parameter
    const baseUrl = `projects/${projectId}/translate/document`;
    const urlWithSession = `${baseUrl}?sessionId=${sessionId}`;

    return this.apiService.post<ITranslateDocumentOutput>(urlWithSession, translateDocParams)
      .pipe(
        tap((result) => {
          // Start SSE connection for translation progress
          this.startTranslationProgressTracking(projectId, translateDocParams.fileId, result.jobId, filename, result.from, result.to, sessionId);
        })
      );
  }

  /**
   * Starts SSE-based progress tracking for document translation.
   *
   * @param projectId
   * @param fileId
   * @param jobId
   * @param filename
   * @param sourceLanguage
   * @param targetLanguage
   * @param sessionId
   */
  private startTranslationProgressTracking(projectId: string, fileId: string, jobId: string, filename: string, sourceLanguage: string, targetLanguage: string, sessionId: string): void {
    const baseJobData = {
      id: jobId,
      sourceLanguage,
      name: filename,
      targetLanguage,
      projectId,
      timestamp: Date.now()
    };

    this.fileProcessingService.watchProgress(projectId, 'translation', sessionId, fileId)
      .pipe(
        tap((progress) => {
          // Handle progress updates
          this.updateJob(jobId, {
            ...baseJobData,
            status: this.mapFileProcessingStatusToTranslationJobStatus(progress.status),
            progress: progress.processed
          });

          // Handle completion
          if (progress.status === FileProcessingStatus.COMPLETED) {
            this.updateJob(jobId, {
              ...baseJobData,
              status: TranslationJobStatus.COMPLETED,
              progress: 100,
              newFileId: progress.newFileId
            });
          }
        }),
        catchError(() => {
          this.updateJob(jobId, {
            ...baseJobData,
            status: TranslationJobStatus.FAILED,
            progress: 0
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Gets active translation jobs from the server.
   *
   * @param projectId
   * @param options
   * @returns
   */
  public getActiveTranslationJobs(projectId: string, options?: IListOptions): Observable<IAPIListResponse<ITranslationAttempt>> {
    return this.apiService.list<IAPIListResponse<ITranslationAttempt>>(`projects/${projectId}/translate/active-jobs`, undefined, options);
  }

  /**
   * Adds a job to the local state.
   *
   * @param job
   */
  public addJob(job: ITranslationAttempt): void {
    const jobs = this.activeDocumentTranslation();

    const newJob = {
      type: 'TRANSLATE_DOCUMENT',
      ...job
    };

    this.activeDocumentTranslation.set([...jobs, newJob]);
  }

  /**
   * Watch the current active document translations.
   *
   * @returns The current active document translations as read-only signal.
   */
  public watchActiveDocumentTranslation(): Signal<ITranslationAttempt[]> {
    return this.activeDocumentTranslation.asReadonly();
  }

  /**
   * Updates the status of a specific translation attempt.
   *
   * @param jobId
   * @param updates
   */
  public updateJob(jobId: string, updates: Partial<ITranslationAttempt>): void {
    const jobs = this.activeDocumentTranslation();

    const index = jobs.findIndex(job => job.id === jobId);

    if (index < 0) {
      jobs.push(updates as ITranslationAttempt);
      this.activeDocumentTranslation.set([...jobs]);

      return;
    }

    jobs[index] = { ...jobs[index], ...updates };

    this.activeDocumentTranslation.set([...jobs]);
  }

  /**
   * Gets a specific translation job by ID.
   *
   * @param jobId
   * @returns
   */
  public getJob(jobId: string): ITranslationAttempt | undefined {
    return this.activeDocumentTranslation().find(job => job.id === jobId);
  }

  /**
   * Maps FileProcessingStatus to TranslationJobStatus.
   *
   * @param status The FileProcessingStatus value to convert.
   * @returns The corresponding TranslationJobStatus value.
   */
  private mapFileProcessingStatusToTranslationJobStatus(status: FileProcessingStatus): TranslationJobStatus {
    switch (status) {
      case FileProcessingStatus.PENDING:
        return TranslationJobStatus.PENDING;
      case FileProcessingStatus.IN_PROGRESS:
      case FileProcessingStatus.PREPARING:
      case FileProcessingStatus.ZIPPING:
      case FileProcessingStatus.DOWNLOADING:
        return TranslationJobStatus.ACTIVE;
      case FileProcessingStatus.COMPLETED:
        return TranslationJobStatus.COMPLETED;
      case FileProcessingStatus.FAILED:
        return TranslationJobStatus.FAILED;
      default:
        return TranslationJobStatus.PENDING;
    }
  }

  /**
   * Retrieves stored translation attempts from the server.
   *
   * @param fileId
   */
  private fetchProjectTranslateDocumentJob(fileId?: string): void {
    this.activeDocumentTranslation.set([]);

    if (!this.projectId) {
      return;
    }

    const options = this.listService.generateListOptionsForSearch(fileId ?? '');

    this.getActiveTranslationJobs(this.projectId, options).pipe(take(1)).subscribe(result => {
      let jobs: ITranslationAttempt[] = [];

      if (result.data) {
        jobs = result.data.map((job) => {
          if (job.file) {
            job.file = FileService.hydrateFile(job.file);
          }

          return job;
        });
      }

      this.activeDocumentTranslation.set(jobs);
    });
  }
}
